import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { BRAND, PACKAGES } from '../utils/constants';
import useSEO from '../hooks/useSEO';
import './Packages.css';

const COMPARISON = [
  { feature: 'Custom Meal Plan', online: true, inperson: true, dropin: false },
  { feature: 'Accountability Group', online: true, inperson: true, dropin: false },
  { feature: 'Progress Tracking', online: true, inperson: true, dropin: false },
  { feature: 'Online Sessions', online: true, inperson: true, dropin: false },
  { feature: 'In-Person Access', online: false, inperson: true, dropin: 'Per class' },
  { feature: '5 Days Training', online: false, inperson: true, dropin: false },
  { feature: 'WhatsApp Support', online: true, inperson: true, dropin: false },
  { feature: 'Bi-weekly Assessments', online: true, inperson: true, dropin: false },
  { feature: 'No Commitment Required', online: false, inperson: false, dropin: true },
];

function ExpandableSection({ pkg }) {
  const [open, setOpen] = useState(pkg.id === 'inperson');
  return (
    <div className={`expand-section ${open ? 'open' : ''} ${pkg.color === 'accent' ? 'featured' : ''}`}>
      <button className="expand-header" onClick={() => setOpen((v) => !v)}>
        <div className="expand-title">
          <h3>{pkg.name}</h3>
          <div className="expand-price">{pkg.price}<span>{pkg.period}</span></div>
          {pkg.note && <span className="expand-note">{pkg.note}</span>}
        </div>
        {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {open && (
        <div className="expand-body">
          {pkg.badge && <div className="expand-badge">{pkg.badge}</div>}
          <ul className="expand-features">
            {pkg.features.map((f, i) => (
              <li key={i}><CheckCircle size={14} /> {f}</li>
            ))}
          </ul>
          <Link to={`/book?package=${pkg.id}`} className={`btn ${pkg.color === 'accent' ? 'btn-primary' : 'btn-ghost'}`}>
            {pkg.cta} <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}

function Cell({ val }) {
  if (val === true) return <CheckCircle size={18} className="cell-yes" />;
  if (val === false) return <XCircle size={18} className="cell-no" />;
  return <span className="cell-text">{val}</span>;
}

export default function Packages() {
  const serviceSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Personal Training & Fitness Coaching',
    provider: {
      '@type': 'LocalBusiness',
      name: 'The Fhysique Factory',
      address: { '@type': 'PostalAddress', streetAddress: '6-8 Parkington Plaza, Maths Unlimited Building', addressLocality: 'Kingston', addressCountry: 'JM' },
    },
    areaServed: { '@type': 'City', name: 'Kingston', '@id': 'https://www.wikidata.org/wiki/Q34692' },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Training Packages',
      itemListElement: [
        { '@type': 'Offer', name: 'Online Training', price: '10000', priceCurrency: 'JMD', description: 'Custom meal plan, bi-weekly assessments, accountability group, morning & evening sessions.' },
        { '@type': 'Offer', name: 'In-Person Training', price: '15000', priceCurrency: 'JMD', description: '5 days in-person training at Parkington Plaza with expert coaching and priority booking.' },
        { '@type': 'Offer', name: 'Drop-In Classes', price: '1500', priceCurrency: 'JMD', description: 'Step, Soca Abs, Powerlifting classes — no commitment, pay per session.' },
        { '@type': 'Offer', name: '1-on-1 Consultation', price: '2000', priceCurrency: 'JMD', description: '60-minute private session — full assessment, personalized program, sample meal plan.' },
      ],
    },
  }), []);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fysiquefactory.fit/' },
      { '@type': 'ListItem', position: 2, name: 'Packages', item: 'https://fysiquefactory.fit/packages' },
    ],
  }), []);

  useSEO({
    title: 'Gym Membership Prices Kingston Jamaica | Online & In-Person Training Packages',
    description: 'Affordable personal training packages in Kingston Jamaica. Online training $10,000 JMD/month, in-person $15,000 JMD/month, drop-in classes $1,500/session. Custom meal plans, accountability coaching, and body transformation programs at Parkington Plaza.',
    path: '/packages',
    schema: [serviceSchema, breadcrumbSchema],
  });

  return (
    <div className="page packages-page">
      {/* Header */}
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Transparent Pricing</span>
          <div className="lime-line" />
          <h1 className="section-title">Pick Your Package</h1>
          <p className="section-sub">
            No hidden fees. No long-term contracts. Just real results.{' '}
            <strong className="text-lime">Limited spots — don't miss out.</strong>
          </p>
        </div>
      </section>

      {/* Package cards */}
      <section className="pkg-cards-section">
        <div className="container packages-grid-full">
          {PACKAGES.map((pkg) => (
            <div key={pkg.id} className={`pkg-card-full ${pkg.color === 'accent' ? 'featured' : ''}`}>
              {pkg.badge && <div className="pkg-badge-full">{pkg.badge}</div>}
              <div className="pkg-header">
                <h3>{pkg.name}</h3>
                <div className="pkg-price-full">{pkg.price}<span>{pkg.period}</span></div>
                {pkg.note && <p className="pkg-note-full">{pkg.note}</p>}
              </div>
              <ul className="pkg-features-full">
                {pkg.features.map((f, i) => (
                  <li key={i}><CheckCircle size={14} /> {f}</li>
                ))}
              </ul>
              <Link to={`/book?package=${pkg.id}`} className={`btn btn-full ${pkg.color === 'accent' ? 'btn-primary' : 'btn-ghost'}`}>
                {pkg.cta} <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="comparison-section">
        <div className="container">
          <span className="section-label">Feature Comparison</span>
          <div className="lime-line" />
          <h2 className="section-title">What's Included</h2>
          <div className="table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Online<br /><span>$10,000/mo</span></th>
                  <th className="featured-col">In-Person<br /><span>$15,000/mo</span></th>
                  <th>Drop-In<br /><span>$1,500/class</span></th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td><Cell val={row.online} /></td>
                    <td className="featured-col"><Cell val={row.inperson} /></td>
                    <td><Cell val={row.dropin} /></td>
                  </tr>
                ))}
                <tr className="cta-row">
                  <td></td>
                  <td>
                    <Link to="/contact?package=online" className="btn btn-ghost btn-sm">Choose Online</Link>
                  </td>
                  <td className="featured-col">
                    <Link to="/contact?package=inperson" className="btn btn-primary btn-sm">Choose In-Person</Link>
                  </td>
                  <td>
                    <Link to="/contact?package=dropin" className="btn btn-ghost btn-sm">Book Now</Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Expandable detail sections */}
      <section className="detail-section">
        <div className="container">
          <span className="section-label">Full Details</span>
          <div className="lime-line" />
          <h2 className="section-title">Everything You Get</h2>
          <div className="expand-list">
            {PACKAGES.map((pkg) => (
              <ExpandableSection key={pkg.id} pkg={pkg} />
            ))}
          </div>
        </div>
      </section>

      {/* Policies */}
      <section className="policies-section">
        <div className="container policies-inner">
          <h3>Policies</h3>
          <ul>
            <li>All sessions start promptly — please arrive 5 minutes early.</li>
            <li>Fees are non-refundable once the month begins.</li>
            <li>In-Person access card: one-time $1,000 JMD fee (non-refundable).</li>
            <li>Drop-in classes must be paid before the session begins.</li>
          </ul>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="packages-cta">
        <div className="container packages-cta-inner">
          <h2>Still Have Questions?</h2>
          <p>Reach out directly — we'll help you pick the right package.</p>
          <div className="packages-cta-btns">
            <a
              href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I want to know more about your packages.`}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-whatsapp btn-lg"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a href={`tel:${BRAND.phone}`} className="btn btn-primary btn-lg">
              <Phone size={18} /> Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
